import { useCallback, useState, useMemo, useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $createTextNode, $getRoot, $getSelection, $isRangeSelection } from 'lexical';
import {
  AgxButton,
  AgxModal,
  AgxRow,
  AgxColumn,
  AgxTextInput,
  Icon_Link,
} from '@urbanx/agx.ui-components2';

const isValidUrl = (url: string) => {
  if (!url.trim()) return false;
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

interface AddLinkPluginProps {
  disabled?: boolean;
  maxLength?: number;
}

const AddLinkPlugin = ({ disabled, maxLength }: AddLinkPluginProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [url, setURL] = useState('');
  const [linkText, setLinkText] = useState('');
  const [characterError, setCharacterError] = useState('');
  const [editor] = useLexicalComposerContext();

  const resetModal = useCallback(() => {
    setURL('');
    setLinkText('');
    setCharacterError('');
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (!maxLength) return;

    editor.getEditorState().read(() => {
      const textContent = $getRoot().getTextContent();
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        const textToInsert = linkText || url;
        const selectedLength = selection.getTextContent().length;
        const newLength = textContent.length - selectedLength + textToInsert.length;

        if (newLength > maxLength) {
          const remainingChars = maxLength - (textContent.length - selectedLength);
          setCharacterError(
            remainingChars <= 0
              ? 'Cannot add link - character limit reached'
              : `Link text too long. Only ${remainingChars} characters remaining.`
          );
        } else {
          setCharacterError('');
        }
      }
    });
  }, [editor, linkText, url, maxLength]);

  const onAddLink = useCallback(async () => {
    editor.focus();
    await new Promise((resolve) => requestAnimationFrame(resolve));

    editor.update(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        const textToInsert = linkText || url;
        const normalizedUrl = url.startsWith('http') ? url : `https://${url}`;

        const textNode = $createTextNode(textToInsert);
        selection.insertNodes([textNode]);

        selection.setTextNodeRange(textNode, 0, textNode, textToInsert.length);

        editor.dispatchCommand(TOGGLE_LINK_COMMAND, { url: normalizedUrl });

        selection.setTextNodeRange(textNode, textToInsert.length, textNode, textToInsert.length);
        selection.insertText(' ');
      }
    });

    resetModal();
  }, [editor, linkText, url, resetModal]);

  const handleLinkTextChange = useCallback(
    (value: string) => {
      setLinkText(value);
      if (characterError) setCharacterError('');
    },
    [characterError]
  );

  const handleUrlChange = useCallback(
    (value: string) => {
      setURL(value);
      if (characterError) setCharacterError('');
    },
    [characterError]
  );

  const isValid = useMemo(() => isValidUrl(url), [url]);

  const modalContent = (
    <div>
      <AgxColumn gap="spacing-3xl" padding={{ top: 'md', bottom: 'md', left: 'xl', right: 'xl' }}>
        <AgxTextInput
          id="link-url"
          label="Paste your link here *"
          type="text"
          hideOptionalText
          onChange={handleUrlChange}
          defaultValue={url}
          required
          stretch
          errorText={
            url && !isValid ? 'Please enter a valid link' : characterError ? characterError : ''
          }
        />
        <AgxTextInput
          id="link-text"
          label="Display text"
          type="text"
          onChange={handleLinkTextChange}
          defaultValue={linkText}
          required
          stretch
          errorText={characterError}
        />
      </AgxColumn>
    </div>
  );

  const actionButtons = (
    <AgxRow justifyContent="center" gap="spacing-lg" fill>
      <AgxButton
        id="cancel-link"
        variant="hollow"
        size="large"
        text="Cancel"
        onClick={resetModal}
      />
      <AgxButton
        id="add-link"
        variant="primary"
        size="large"
        text="Add Link"
        onClick={onAddLink}
        disabled={!isValid || !!characterError}
      />
    </AgxRow>
  );

  return (
    <>
      <AgxButton
        id="add-link-button"
        variant="naked"
        size="small"
        text={
          <Icon_Link
            width={16}
            height={16}
            color={`${disabled ? 'var(--fg-disabled)' : 'var(--fg-brand-accent)'}`}
          />
        }
        onClick={() => setIsOpen(true)}
        disabled={disabled}
      />
      {isOpen && (
        <AgxModal
          title="Add Link"
          type="single"
          variant="withDivider"
          setIsDialogOpen={setIsOpen}
          content={modalContent}
          actionButtonContainerStyle="withoutDivider"
          actionButtons={actionButtons}
        />
      )}
    </>
  );
};

export default AddLinkPlugin;
